<template>
  <div>
    <b-row>
      <b-col cols="12">
        <b-button
          @click="$router.push('/trading/trading-dashboard')"
          variant="outline-secondary"
          size="sm"
        >
          Trading Dashboard
        </b-button>
      </b-col>
    </b-row>

    <b-row class="mt-1 mb-1">
      <b-col
        cols="12"
        class="d-flex align-items-center justify-content-between"
      >
        <h4 class="font-weight-bolder mb-0">BET MONITOR</h4>
        <div class="d-flex align-items-center" style="gap: 8px">
          <span class="text-nowrap font-weight-bold">Rows per page</span>
          <b-form-select
            v-model="limit"
            :options="limitOptions"
            size="sm"
            style="width: 80px"
          />
        </div>
      </b-col>
    </b-row>

    <b-row class="mt-1">
      <b-col cols="12">
        <b-card>
          <b-row>
            <!-- Date Preset Filter -->
            <b-col cols="4" md="2">
              <b-form-select
                v-model="datePreset"
                :options="datePresetOptions"
                @change="onDatePresetChange"
              />
              <flat-pickr
                v-if="datePreset === 'custom'"
                v-model="filters.rangeTime"
                class="form-control mt-1"
                :config="config"
                placeholder="From - To"
              />
            </b-col>

            <b-col cols="4" md="2">
              <b-form-input
                v-model="search"
                placeholder="Search by name or email address"
                class="w-100"
              />
            </b-col>

            <b-col cols="4" md="2">
              <b-dropdown
                id="status-filter"
                :text="titleResult"
                variant="outline-dark"
                menu-class="p-1 w-100"
                class="w-100"
              >
                <!-- Clear Filter -->
                <p
                  class="text-danger"
                  style="
                    text-decoration: underline;
                    cursor: pointer;
                    margin-bottom: 8px;
                  "
                  @click="clearFilterResult"
                >
                  Clear Filter
                </p>

                <div
                  v-for="opt in resultOptions"
                  :key="opt.value"
                  style="padding: 4px 0"
                >
                  <b-form-checkbox :value="opt.value" v-model="filters.results">
                    <span class="text-nowrap">{{ opt.text }}</span>
                  </b-form-checkbox>
                </div>
              </b-dropdown>
            </b-col>

            <b-col cols="4" md="2">
              <b-form-select
                v-model="filters.betType"
                :options="betTypeOptions"
                class="w-100"
              />
            </b-col>
            <b-col cols="4" md="2">
              <b-form-input
                v-model.number="filters.minStake"
                type="number"
                placeholder="Stake Amount (min)"
                class="w-100"
              />
            </b-col>
            <b-col cols="4" md="2">
              <b-form-input
                v-model.number="filters.minLiability"
                type="number"
                placeholder="Liability Amount (min)"
                class="w-100"
              />
            </b-col>

            <b-col cols="4" md="2">
              <b-button
                variant="primary"
                class="mt-1 w-100 text-nowrap mr-1"
                @click="applyFilters"
              >
                <feather-icon icon="FilterIcon" />
                Apply filters
              </b-button>
            </b-col>
            <b-col cols="4" md="2">
              <b-button
                variant="light"
                class="mt-1 w-100 text-nowrap"
                @click="clearFilters"
              >
                <feather-icon icon="XIcon" />
                Clear filter
              </b-button>
            </b-col>
            <b-col cols="4" md="2">
              <b-button
                variant="success"
                class="mt-1 w-100 text-nowrap"
                :disabled="exportingCSV"
                @click="exportCSV"
              >
                <b-spinner v-if="exportingCSV" small />
                <feather-icon v-else icon="DownloadIcon" />
                {{ exportingCSV ? "Exporting..." : "Export CSV" }}
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="mt-1">
      <b-col cols="12">
        <b-card>
          <b-table
            ref="refBetslipTable"
            :fields="fields"
            responsive
            :items="betslips"
            :busy="loadingInitial"
            thead-class="text-nowrap"
            show-empty
            sticky-header="60vh"
          >
            <template #cell(date_time)="{ item }">
              <b-media class="text-nowrap">{{
                format(new Date(item.createdTime), "dd/MM/yyyy hh:mm:ss")
              }}</b-media>
            </template>
            <template #cell(bet_id)="{ item }">
              <b-link href="#" class="font-weight-bolder">{{
                item.displayId
              }}</b-link>
            </template>
            <template #cell(customer)="{ item }">
              <div>
                <div class="font-weight-bold text-nowrap">
                  {{ item.name || item.username }}
                </div>
                <small class="text-muted">{{ item.email || "-" }}</small>
              </div>
            </template>

            <template #cell(bet_type)="{ item }">
              <b-media class="text-nowrap">
                <template v-if="item.type === 'multi'">
                  Multi ({{ item.selections ? item.selections.length : 0 }})
                </template>
                <template v-else>{{ item.type }}</template>
              </b-media>
            </template>
            <template #cell(promo)="{ item }">
              <b-media class="text-nowrap">
                <b-badge
                  v-if="
                    item.currencyMethod === 'bonus' ||
                    item.promotions.length > 0
                  "
                  :variant="
                    item.currencyMethod === 'bonus' ? 'primary' : 'success'
                  "
                  >{{
                    item.currencyMethod !== "bonus" &&
                    item.promotions.length > 0
                      ? "Boost"
                      : item.currencyMethod
                  }}</b-badge
                >
              </b-media>
            </template>
            <template #cell(sport)="{ item }">
              <div style="width: 150px">
                {{ convertSportDescriptive(item) }}
              </div>
            </template>

            <template #cell(state)="{ item }">
              <div>
                <p class="" style="width: 150px">{{ convertState(item) }}</p>
              </div>
            </template>
            <template #cell(event)="{ item }">
              <div style="min-width: 150px">
                <template v-if="item.type === 'multi'">
                  Multi ({{ item.selections ? item.selections.length : 0 }})
                </template>
                <template v-else>{{ convertEvent(item) }}</template>
              </div>
            </template>
            <template #cell(selection)="row">
              <template v-if="row.item.type === 'multi'">
                <div
                  v-if="row.item.selections && row.item.selections.length > 0"
                  style="min-width: 200px"
                >
                  <div
                    class="text-muted small"
                    style="max-width: 220px; white-space: normal"
                  >
                    {{ getMultiSelectionSummary(row.item) }}
                  </div>
                  <b-button
                    size="sm"
                    variant="outline"
                    @click="row.toggleDetails"
                    class="p-0 mt-25"
                    style="font-size: 12px"
                  >
                    <feather-icon
                      icon="ChevronDownIcon"
                      :class="row.detailsShowing ? 'rotate-180' : ''"
                      style="transition: transform 0.3s"
                    />
                    View all legs
                  </b-button>
                </div>
                <b-media v-else> - </b-media>
              </template>
              <template v-else-if="row.item.type === 'single'">
                <template
                  v-if="row.item.selections && row.item.selections.length > 0"
                >
                  <template
                    v-if="row.item.selections[0].hasOwnProperty('meetingId')"
                  >
                    <b-media class="text-nowrap">
                      {{ row.item.selections[0].runnerNo }}.
                      {{ row.item.selections[0].runnerName }} ({{
                        row.item.selections[0].barrierPosition
                      }})</b-media
                    >
                  </template>
                  <template v-else>
                    <span class="text-muted">{{
                      row.item.selections[0].name
                    }}</span>
                  </template>
                </template>
                <b-media v-else> - </b-media>
              </template>
              <template v-else>
                <b-button
                  v-if="row.item.selections && row.item.selections.length > 0"
                  size="md"
                  variant="outline"
                  @click="row.toggleDetails"
                  class="text-nowrap p-0 text-capitalize"
                  style="font-size: 14px"
                >
                  <feather-icon
                    icon="ChevronDownIcon"
                    :class="row.detailsShowing ? 'rotate-180' : ''"
                    style="transition: transform 0.3s; margin-right: 10px"
                  />{{ row.item.type }} Bets</b-button
                >
                <b-media v-else> - </b-media>
              </template>
            </template>

            <template #row-details="row">
              <b-card body-class="p-0">
                <div v-if="row.item.type === 'multi'">
                  <div v-for="(sel, index) in row.item.selections" :key="index">
                    <strong>Leg {{ index + 1 }}:</strong>
                    <div class="ml-2 d-inline">
                      <template v-if="sel && sel.hasOwnProperty('meetingId')">
                        <span v-if="sel.raceNumber" class="font-weight-bolder">
                          Race {{ sel.raceNumber }} - {{ sel.meetingName }}
                          {{
                            format(
                              new Date(sel.raceStartTime),
                              "dd MMM yyyy @ hh:mm"
                            )
                          }}
                        </span>
                        <span>{{ convertSelectionType(sel) }}</span>
                        <span
                          v-if="sel.runnerNo"
                          class="font-weight-bolder ml-1"
                        >
                          {{ sel.runnerNo }}. {{ sel.runnerName }} ({{
                            sel.barrierPosition
                          }})
                        </span>
                        <span class="ml-1">@</span>
                        <span class="font-weight-bolder ml-1">
                          {{ formatCurrencyBuiltInFunc(sel.odds) }}
                        </span>
                      </template>
                      <template v-else>
                        <span class="font-weight-bolder"
                          >{{ sel.eventTitle }}
                          {{
                            format(
                              new Date(sel.eventStartTime),
                              "dd MMM yyyy @ hh:mm"
                            )
                          }}</span
                        >
                        <span class="text-muted ml-1"
                          >({{ sel.marketName }})</span
                        >
                        <span class="font-weight-bolder ml-1"
                          >({{ sel.name }})</span
                        >
                        <span class="ml-1">@</span>
                        <span class="font-weight-bolder ml-1">
                          {{ formatCurrencyBuiltInFunc(sel.odds) }}
                        </span>
                      </template>
                    </div>
                  </div>
                </div>

                <!-- Exotic Bets (Trifecta, Exacta, etc.) -->
                <div v-else-if="isExoticBetType(row.item.type)">
                  <div v-for="i in 4" :key="i">
                    <div
                      v-if="
                        row.item.selections.filter((s) => s[`place${i}`] === 1)
                          .length
                      "
                      class="mb-2 p-1"
                    >
                      <h6 class="font-weight-bold">
                        {{ getExoticPlaceLabel(i) }}:
                      </h6>

                      <div
                        v-for="sel in row.item.selections.filter(
                          (s) => s[`place${i}`] === 1
                        )"
                        :key="sel.id"
                        class="bg-white d-flex align-items-center"
                        style="
                          padding: 5px;
                          margin-bottom: 5px;
                          border-bottom: 1px solid #dcdfe3 !important;
                        "
                      >
                        <div
                          class="d-flex align-items-center"
                          style="gap: 10px"
                        >
                          <!-- Silk -->
                          <img
                            style="width: 36px; height: 36px; object-fit: cover"
                            :src="sel.silkStrip"
                            :style="{
                              objectPosition: sel.runnerNo
                                ? `-${36 * (sel.runnerNo - 1)}px 0`
                                : '0',
                            }"
                          />

                          <!-- Runner Info -->
                          <div
                            class="d-flex align-items-center"
                            style="gap: 10px"
                          >
                            <span class="font-weight-bold">
                              {{ sel.runnerNo }}. {{ sel.runnerName }}
                              {{
                                sel.barrierPosition
                                  ? `(${sel.barrierPosition})`
                                  : ""
                              }}
                            </span>

                            <!-- Result Icon -->
                            <div
                              v-if="sel.raceStatus === 'RESULT'"
                              class="d-flex align-items-center justify-content-center"
                              :style="{
                                background:
                                  checkResultBetTypeCombo(sel, row.item, i) ===
                                  'Lose'
                                    ? '#B00606'
                                    : '#007800',
                                borderRadius: '100%',
                                width: '25px',
                                height: '25px',
                                rotate:
                                  checkResultBetTypeCombo(sel, row.item, i) ===
                                  'Lose'
                                    ? '180deg'
                                    : '0deg',
                              }"
                            >
                              <img
                                class="d-block img-fluid"
                                width="15"
                                height="15"
                                :src="
                                  require('@/assets/images/icons/thumb-icon.svg')
                                "
                                alt="thumb-icon"
                              />
                            </div>
                          </div>
                        </div>

                        <!-- Right Label -->
                        <span class="text-muted" style="font-size: 0.9em">
                          {{ getExoticPlaceLabel(i) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Other Bets (Group by Race) -->
                <div v-else>
                  <div
                    v-for="(race, index) in getGroupedSelections(
                      row.item.selections
                    )"
                    :key="index"
                    class="bg-white"
                    style="
                      padding: 5px;
                      margin-bottom: 5px;
                      border-bottom: 1px solid #dcdfe3 !important;
                    "
                  >
                    <!-- Race Header -->
                    <div class="d-flex align-items-center" style="gap: 50px">
                      <h6 class="m-0 font-weight-bold">
                        Race {{ race.raceNumber }}:
                      </h6>
                      <small class="text-muted">
                        {{
                          format(
                            new Date(race.raceStartTime),
                            "dd/MM/yyyy HH:mm"
                          )
                        }}
                      </small>
                    </div>

                    <!-- Runners -->
                    <div
                      v-for="sel in race.selections"
                      :key="sel.id"
                      class="d-flex align-items-center"
                      style="padding: 5px; gap: 50px"
                    >
                      <div class="d-flex align-items-center" style="gap: 10px">
                        <!-- Silk -->
                        <img
                          style="width: 36px; height: 36px; object-fit: cover"
                          :src="sel.silkStrip"
                          :style="{
                            objectPosition: sel.runnerNo
                              ? `-${36 * (sel.runnerNo - 1)}px 0`
                              : '0',
                          }"
                        />

                        <!-- Runner Details -->
                        <div class="flex-grow-1">
                          <span class="font-weight-bold">
                            {{ sel.runnerNo }}. {{ sel.runnerName }}
                          </span>
                          <span class="text-muted" v-if="sel.barrierPosition">
                            ({{ sel.barrierPosition }})
                          </span>
                        </div>
                      </div>

                      <div
                        v-if="sel.raceStatus === 'RESULT'"
                        class="d-flex align-items-center justify-content-center"
                        :style="{
                          background:
                            checkResultBetTypeCombo(sel, race) === 'Lose'
                              ? '#B00606'
                              : '#007800',
                          borderRadius: '100%',
                          width: '25px',
                          height: '25px',
                          rotate:
                            checkResultBetTypeCombo(sel, race) === 'Lose'
                              ? '180deg'
                              : '0deg',
                        }"
                      >
                        <img
                          class="d-block img-fluid"
                          width="15"
                          height="15"
                          :src="require('@/assets/images/icons/thumb-icon.svg')"
                          alt="thumb-icon"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </b-card>
            </template>

            <template #cell(stake)="{ item }">
              <b-media class="text-nowrap">{{
                formatCurrencyBuiltInFunc(item.stake)
              }}</b-media>
            </template>
            <template #cell(market_type)="row">
              <template v-if="row.item.type === 'single'">
                <b-media
                  class="text-nowrap"
                  v-if="row.item.selections && row.item.selections.length > 0"
                >
                  {{
                    row.item.selections[0].selectionType ||
                    row.item.selections[0].marketType ||
                    "-"
                  }}
                </b-media>
                <b-media v-else> - </b-media>
              </template>
              <template v-else>
                <b-media> {{ row.item.type }} </b-media>
              </template>
            </template>

            <template #cell(wallet_used)="{ item }">
              <b-badge
                :variant="
                  item.currencyMethod === 'bonus' ? 'primary' : 'success'
                "
                class="text-capitalize"
                >{{ item.currencyMethod || "cash" }}</b-badge
              >
            </template>

            <template #cell(liability)="{ item }">
              <b-media class="text-nowrap">
                {{
                  !!item.odds && !!item.stake
                    ? formatCurrencyBuiltInFunc(item.stake * item.odds - item.stake)
                    : "TBD"
                }}
              </b-media>
            </template>

            <template #cell(result)="{ item }">
              <b-badge :variant="resultVariant(item.result)">{{
                item.result || "Unresulted"
              }}</b-badge>
            </template>
            <!-- STATUS badge -->
            <template #cell(status)="{ item }">
              <b-badge
                :variant="statusVariant(item.status)"
                class="text-capitalize"
                >{{
                  item.status === "declined" &&
                  item.cancelledBy === "Race abandoned"
                    ? "Fully Refunded"
                    : item.status
                }}</b-badge
              >
            </template>

            <!-- ACTIONS button -->
            <template #cell(actions)="data">
              <b-button
                size="sm"
                variant="outline-primary"
                @click="openModalInfoBet(data.item)"
                >Manage</b-button
              >
            </template>
          </b-table>

          <!-- Loading more spinner -->
          <div
            v-if="loading && betslips && betslips.length > 0"
            class="text-center py-2"
          >
            <b-spinner small class="mr-1" />
            Loading more...
          </div>

          <!-- End of results -->
          <div
            v-if="!hasMore && betslips && betslips.length > 0"
            class="text-center text-muted small"
          >
            All
            {{ betslips && betslips.length ? betslips.length : 0 }} results
            loaded
          </div>

          <b-row>
            <b-col cols="12" class="text-muted small">
              {{ paginationSummary }}
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>

    <ManageBetslip :selectedBet="selectedBet" @refresh="fetchBetslips" />
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BContainer,
  BRow,
  BBadge,
  BLink,
  BButton,
  BTable,
  BFormSelect,
  BFormInput,
  BModal,
  BFormGroup,
  BMedia,
  BDropdown,
  BFormCheckbox,
  BSpinner,
} from "bootstrap-vue";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import { mapState, mapActions } from "vuex";
import { debounce } from "debounce";
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  startOfMonth,
  subDays,
  format,
} from "date-fns";
import { ref } from "@vue/composition-api";
import useToastHandler from "@/services/toastHandler";
import UpdateResultModal from "./sports/UpdateResultModal.vue";
import ManageBetslip from "./ManageBetslipV2.vue";
import flatPickr from "vue-flatpickr-component";
export default {
  components: {
    BCard,
    BCol,
    BContainer,
    BRow,
    BBadge,
    BLink,
    BButton,
    BTable,
    BFormSelect,
    BFormInput,
    BModal,
    BFormGroup,
    BMedia,
    UpdateResultModal,
    ManageBetslip,
    flatPickr,
    BDropdown,
    BFormCheckbox,
    BSpinner,
  },
  data() {
    const resultOptions = [
      { text: "Resulted Win", value: "resulted-win" },
      {
        text: "Resulted Loss",
        value: "resulted-loss",
      },
      { text: "Cancelled", value: "bet-cancelled" },
      { text: "Unresulted", value: "bet-pending" },
      { text: "Rejected", value: "rejected" },
      {
        text: "Fully Refunded",
        value: "bet-refunded",
      },
    ];
    const betTypeOptions = [
      { text: "All Bet Types", value: null },
      { text: "Single", value: "single" },
      { text: "Multi", value: "multi" },
      { text: "Quinella", value: "quinella" },
      { text: "Exacta", value: "exacta" },
      { text: "Trifecta", value: "trifecta" },
      { text: "First 4", value: "first4" },
      { text: "Daily Double", value: "daily-double" },
      { text: "Running Double", value: "running-double" },
      { text: "Treble", value: "treble" },
      // { text: "SRM", value: "srm" },
      { text: "SGM", value: "sgm" },
      { text: "Quaddie", value: "quaddie" },
      { text: "Combo Racing", value: "combo-racing" },
    ];
    return {
      search: "",
      limit: 100,
      limitOptions: [10, 25, 100, 500],
      selectedBet: null,
      datePreset: "today",
      datePresetOptions: [
        { value: "today", text: "Today" },
        { value: "this-week", text: "This Week" },
        { value: "this-month", text: "This Month" },
        { value: "last-60-days", text: "Last 60 Days" },
        { value: "custom", text: "Custom Range" },
      ],
      filters: {
        rangeTime: "",
        results: [],
        betType: null,
        minStake: null,
        minLiability: null,
      },
      betTypeOptions,
      titleResult: "All Bet Statuses",
      resultOptions,
      config: {
        mode: "range",
        altFormat: "d-m-Y",
        altInput: true,
      },
      fields: [
        { key: "date_time", label: "DATE/TIME" },
        { key: "bet_id", label: "BET ID" },
        { key: "customer", label: "CUSTOMER" },
        { key: "bet_type", label: "BET TYPE" },
        { key: "promo", label: "PROMO" },
        { key: "sport", label: "CODE/SPORT" },
        { key: "state" },
        { key: "event", label: "EVENT" },
        { key: "selection", label: "SELECTION", thStyle: "min-width: 220px" },
        { key: "market_type", label: "MARKET TYPE" },
        { key: "wallet_used", label: "WALLET USED" },
        { key: "stake", label: "STAKE" },
        { key: "liability", label: "LIABILITY" },
        { key: "result", label: "BET STATUS" },
        { key: "actions", label: "ACTIONS" },
      ],
      exportingCSV: false,
      modal: {
        title: "",
        content: "",
      },
      action: {
        type: "",
        id: "",
      },
      selectedSelection: null,
      scrollObserver: null,
    };
  },
  computed: {
    ...mapState("trading/list", {
      loading: "loadingBetslips",
      betslips: "betslips",
      hasMore: "hasMore",
    }),
    ...mapState("trading/betslip", ["loadingUpdateStatusBetslip"]),
    isLoading() {
      return this.loadingUpdateStatusBetslip;
    },
    loadingInitial() {
      return this.loading && this.betslips.length === 0;
    },
    paginationSummary() {
      if (this.betslips.length === 0) return "No results";
      return `${this.betslips.length} results loaded`;
    },
    computedDateRange() {
      const now = new Date();
      switch (this.datePreset) {
        case "today":
          return { start: startOfDay(now), end: endOfDay(now) };
        case "this-week":
          return {
            start: startOfWeek(now, { weekStartsOn: 1 }),
            end: endOfDay(now),
          };
        case "this-month":
          return { start: startOfMonth(now), end: endOfDay(now) };
        case "last-60-days":
          return { start: startOfDay(subDays(now, 59)), end: endOfDay(now) };
        case "custom":
          if (this.filters.rangeTime) {
            const [startDate, endDate] =
              this.filters.rangeTime.split(" to ") || [];
            if (startDate && endDate) {
              return {
                start: startOfDay(new Date(startDate)),
                end: endOfDay(new Date(endDate)),
              };
            }
          }
          return null;
        default:
          return null;
      }
    },
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
    this.fetchBetslips();
    this.$nextTick(() => {
      this.setupScrollObserver();
    });
  },
  beforeDestroy() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
    if (this._sentinel) {
      this._sentinel.remove();
    }
  },
  watch: {
    search() {
      this.handleSearchDebounced();
    },
    limit() {
      this.fetchBetslips();
    },
    betslips() {
      if (
        this.selectedBet &&
        this.betslips.some((i) => i.id === this.selectedBet.id)
      ) {
        this.selectedBet = this.betslips.find(
          (i) => i.id === this.selectedBet.id
        );
      }
    },
    "filters.results"(newResult) {
      switch (newResult.length) {
        case 0:
          this.titleResult = "All Bet Statuses";
          break;
        case 1:
          const opt = this.resultOptions.find((o) => o.value === newResult[0]);
          this.titleResult = opt ? opt.text : "All Results";
          break;
        default:
          this.titleResult = "Multi";
      }
    },
  },
  methods: {
    ...mapActions({
      asyncBetslips: "trading/list/getBetslips",
      asyncLoadMoreBetslips: "trading/list/loadMoreBetslips",
      getAllBetslipsForExport: "trading/list/getAllBetslipsForExport",
      udpateStatusBetslip: "trading/betslip/udpateStatusBetslip",
      udpateStakeOddsBetslip: "trading/betslip/udpateStakeOddsBetslip",
      udpateResultBetslip: "trading/betslip/udpateResultBetslip",
    }),
    formatCurrencyBuiltInFunc,
    format,
    buildFilterParams() {
      const { search, limit, filters } = this;
      const params = {
        limit,
        search: search || undefined,
        type: filters.betType || undefined,
        minStake: filters.minStake || undefined,
        minLiability: filters.minLiability || undefined,
      };
      const dateRange = this.computedDateRange;
      if (dateRange) {
        params["createdTime[startDate]"] = dateRange.start.toISOString();
        params["createdTime[endDate]"] = dateRange.end.toISOString();
      }
      if (filters.results.length) {
        filters.results.forEach((result, index) => {
          params[`results[${index}]`] = result;
        });
      }
      return params;
    },
    setupScrollObserver() {
      // with sticky-header, BTable's root $el IS the div.b-table-sticky-header scroll container
      const wrapper = this.$refs.refBetslipTable?.$el;
      if (!wrapper) return;
      const sentinel = document.createElement("div");
      wrapper.appendChild(sentinel);
      this._sentinel = sentinel;
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && this.hasMore && !this.loading) {
            this.loadMore();
          }
        },
        { root: wrapper, rootMargin: "1px", threshold: 0 }
      );
      this.scrollObserver.observe(sentinel);
    },
    async fetchBetslips() {
      if (this.loading) return;
      const params = this.buildFilterParams();
      try {
        await this.asyncBetslips(params);
      } catch (error) {
        // error handled in store
      }
    },
    async loadMore() {
      if (this.loading || !this.hasMore) return;
      const params = this.buildFilterParams();
      try {
        await this.asyncLoadMoreBetslips(params);
      } catch (error) {
        // error handled in store
      }
    },
    handleSearch() {
      this.fetchBetslips();
    },
    onDatePresetChange() {
      if (this.datePreset !== "custom") {
        this.filters.rangeTime = "";
      }
    },
    openModalInfoBet(bet) {
      this.selectedBet = bet;
      this.$bvModal.show("bet-info");
    },
    closeModalInfoBet() {
      this.$refs.betModal.hide();
    },
    closeModalAmendBet() {
      this.$refs.amendModal.hide();
    },
    statusVariant(status) {
      switch (status) {
        case "active":
          return "success";
        case "declined":
          return "danger";
        case "pending":
          return "warning";
        case "resulted":
          return "info";
        case "approved":
          return "primary";
        default:
          return "secondary";
      }
    },
    resultVariant(result) {
      switch (result) {
        case "resulted-win":
          return "success";
        case "resulted-loss":
          return "danger";
        case "FullyRefunded":
          return "primary";
        case "Void":
          return "secondary";
        case "Draw":
          return "info";
        default:
          return "warning";
      }
    },
    isExoticBetType(betType) {
      const exoticTypes = ["trifecta", "first4", "quinella", "exacta"];
      return exoticTypes.includes(betType);
    },
    formatListRaceNumberQuaddie(l) {
      let list = [];
      l.forEach((selection) => {
        if (!list.some((i) => i.raceNumber === selection.raceNumber)) {
          list.push(selection);
        }
      });
      return list
        .map((i) => `Race ${i.raceNumber} - ${i.meetingName}`)
        ?.join(", ");
    },
    convertEvent(item) {
      if (!Array.isArray(item.selections)) return "";
      if (item.type === "multi") return `Multi (${item.selections.length})`;
      if (item.type === "single") {
        if (item.selections[0].racingType === "future")
          return `${item.selections[0].eventName} - ${item.selections[0].meetingName}`;
        if (item.selections[0].hasOwnProperty("meetingId"))
          return `Race ${item.selections[0].raceNumber} - ${item.selections[0].meetingName}`;
        if (item.selections[0].hasOwnProperty("eventTitle"))
          return `Event: ${item.selections[0].eventTitle} - (${item.selections[0].marketName})`;
      }
      if (
        item.type === "srm" ||
        item.category === "exotic" ||
        this.isExoticBetType(item.type)
      )
        return `Race ${item.selections[0].raceNumber} - ${item.selections[0].meetingName}`;

      if (item.type === "quaddie") {
        const selections = [...item.selections].sort((a, b) => {
          if (a.raceNumber !== b.raceNumber) {
            return a.raceNumber - b.raceNumber;
          }
          return a.runnerNo - b.runnerNo;
        });
        return this.formatListRaceNumberQuaddie(selections);
      }
    },
    getGroupedSelections(selections) {
      if (!selections) return [];

      const groups = {};
      selections.forEach((sel) => {
        const key = sel.raceId || `${sel.meetingName}-${sel.raceNumber}`;
        if (!groups[key]) {
          groups[key] = {
            raceNumber: sel.raceNumber,
            meetingName: sel.meetingName,
            raceStartTime: sel.raceStartTime,
            selections: [],
          };
        }
        groups[key].selections.push(sel);
      });

      return Object.values(groups).sort(
        (a, b) => new Date(a.raceStartTime) - new Date(b.raceStartTime)
      );
    },
    getExoticPlaceLabel(number) {
      if (number === 1) return "WIN";
      if (number === 2) return "TOP 2";
      if (number === 3) return "TOP 3";
      if (number === 4) return "TOP 4";
      return "";
    },
    checkResultBetTypeCombo(l, race, ind) {
      if (!!l.result) return l.result;
      const result =
        (!!ind &&
          ind === l.finalPosition &&
          !!l.finalPosition &&
          race.type !== "quinella" &&
          race.category !== "quaddie") ||
        (race.type === "quinella" &&
          !!ind &&
          ind === l.finalPosition &&
          !!l.finalPosition &&
          ind < 3)
          ? "Win"
          : "Lose";

      return result;
    },
    convertSelectionType(selection) {
      if (selection.selectionType === "top-k") {
        return `(More Places - Top ${selection.topK})`;
      }
      return selection.selectionType ? `(${selection.selectionType})` : "";
    },
    clearFilterResult() {
      this.filters.results = [];
    },
    applyFilters() {
      this.fetchBetslips();
    },
    clearFilters() {
      this.datePreset = "today";
      this.filters = {
        rangeTime: "",
        results: [],
        betType: null,
        minStake: null,
        minLiability: null,
      };
      this.fetchBetslips();
    },
    checkBoxedExotic(list) {
      if (!list || list.length <= 1) {
        return true;
      }

      const firstItem = list[0];
      const place1Value = firstItem.place1;
      const place2Value = firstItem.place2;
      const place3Value = firstItem.place3;
      const place4Value = firstItem.place4;

      return list.every((item) => {
        return (
          item.place1 === place1Value &&
          item.place2 === place2Value &&
          item.place3 === place3Value &&
          item.place4 === place4Value
        );
      });
    },
    convertListToGroupedString(list) {
      const group1 = [];
      const group2 = [];
      const group3 = [];
      const group4 = [];

      for (const item of list) {
        const raceNum = item?.runnerNo;

        if (item.place1 === 1) {
          group1.push(raceNum);
        }
        if (item.place2 === 1) {
          group2.push(raceNum);
        }
        if (item.place3 === 1) {
          group3.push(raceNum);
        }
        if (item.place4 === 1) {
          group4.push(raceNum);
        }
      }
      const str1 = group1.sort((a, b) => a - b).join(",");
      const str2 = group2.sort((a, b) => a - b).join(",");
      const str3 = group3.sort((a, b) => a - b).join(",");
      const str4 = group4.sort((a, b) => a - b).join(",");

      const resultParts = [str1, str2, str3, str4].filter(
        (part) => part !== ""
      );

      if (this.checkBoxedExotic(list)) return str1;

      return resultParts.join(" / ");
    },
    convertSport(item) {
      if (!Array.isArray(item.selections)) return "";
      if (item.type === "single") return item.selections[0].sportReport;

      if (item.category === "multi") {
        let text = [];
        item.selections.forEach((element) => {
          if (
            !!element.sportReport &&
            !text.some((i) => i === element.sportReport)
          )
            text.push(element.sportReport);
        });

        return text.join(" - ");
      }
      return item.selections[0].sportReport;
    },
    convertType(item) {
      if (!Array.isArray(item.selections)) return "";

      if (item.category === "multi") {
        let text = [];
        item.selections.forEach((element) => {
          if (
            !!element.categoryReport &&
            !text.some((i) => i === element.categoryReport)
          )
            text.push(element.categoryReport);
        });

        return text.join(" - ");
      }
      return item.selections[0].categoryReport;
    },
    convertSportDescriptive(item) {
      const codeMap = {
        R: "Thoroughbred",
        G: "Greyhound",
        H: "Harness",
        T: "Thoroughbred",
      };
      const raw = this.convertSport(item);
      return codeMap[raw] || raw || "-";
    },
    getMultiSelectionSummary(item) {
      if (!item.selections || !item.selections.length) return "-";
      return item.selections
        .slice(0, 2)
        .map((sel) => {
          if (sel.hasOwnProperty("meetingId")) {
            return sel.runnerNo
              ? `${sel.runnerNo}. ${sel.runnerName}`
              : sel.runnerName || "-";
          }
          return sel.name || sel.selectionName || "-";
        })
        .join(", ");
    },
    convertSelectionText(item) {
      if (!item.selections || item.selections.length === 0) return "";
      if (item.type === "multi") {
        return item.selections
          .map((s) =>
            s.hasOwnProperty("meetingId")
              ? `${s.runnerNo}. ${s.runnerName} (${s.barrierPosition})`
              : s.name || ""
          )
          .join(", ");
      }
      if (item.type === "single") {
        const s = item.selections[0];
        if (s.hasOwnProperty("meetingId"))
          return `${s.runnerNo}. ${s.runnerName} (${s.barrierPosition})`;
        return s.name || "";
      }
      if (this.isExoticBetType(item.type)) {
        return this.convertListToGroupedString(item.selections);
      }
      return item.selections
        .map((s) =>
          s.hasOwnProperty("meetingId")
            ? `${s.runnerNo}. ${s.runnerName} (${s.barrierPosition})`
            : s.name || ""
        )
        .join(", ");
    },
    async fetchAllForExport() {
      const params = { ...this.buildFilterParams() };
      delete params.limit;
      return this.getAllBetslipsForExport(params);
    },
    async exportCSV() {
      if (this.exportingCSV) return;
      this.exportingCSV = true;
      let items;
      try {
        items = await this.fetchAllForExport();
      } catch (e) {
        this.exportingCSV = false;
        return;
      }
      this.exportingCSV = false;
      if (!items.length) return;
      const headers = [
        "DATE/TIME",
        "BET ID",
        "CUSTOMER",
        "BET TYPE",
        "PROMO",
        "CODE/SPORT",
        "STATE",
        "EVENT",
        "SELECTION",
        "MARKET TYPE",
        "WALLET USED",
        "STAKE",
        "LIABILITY",
        "BET STATUS",
      ];
      const rows = items.map((item) => {
        const betType =
          item.type === "multi"
            ? `Multi (${item.selections ? item.selections.length : 0})`
            : item.type || "-";
        const customer = `${item.name || item.username || "-"}${
          item.email ? ` (${item.email})` : "-"
        }`;
        const promo =
          item.currencyMethod === "bonus"
            ? "bonus"
            : item.promotions && item.promotions.length > 0
            ? "Boost"
            : "-";
        const marketType =
          item.type === "single" &&
          item.selections &&
          item.selections.length > 0
            ? item.selections[0].selectionType ||
              item.selections[0].marketType ||
              "-"
            : item.type || "-";
        const liability = item.odds
          ? formatCurrencyBuiltInFunc(item.stake * item.odds)
          : "-";
        return [
          item.createdTime
            ? format(new Date(item.createdTime), "dd/MM/yyyy HH:mm:ss")
            : "-",
          item.displayId || "-",
          customer,
          betType,
          promo,
          this.convertSportDescriptive(item),
          this.convertState(item) || "-",
          this.convertEvent(item) || "-",
          this.convertSelectionText(item),
          marketType,
          item.currencyMethod || "-",
          item.stake || "-",
          liability,
          item.result || "Unresulted",
        ];
      });
      const csvContent = [headers, ...rows]
        .map((r) =>
          r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `bet-monitor-${format(new Date(), "dd-MM-yyyy")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    convertState(item) {
      if (!Array.isArray(item.selections)) return "";
      if (item.type === "single") return item.selections[0].stateReport;

      if (item.category === "multi") {
        let text = [];
        item.selections.forEach((element) => {
          if (
            !!element.stateReport &&
            !text.some((i) => i === element.stateReport)
          )
            text.push(element.stateReport);
        });

        return text.join(" - ");
      }
      return item.selections[0].stateReport;
    },
  },
  setup() {
    const refBetslipTable = ref(null);
    const { showToastSuccess, showToastError } = useToastHandler();

    return {
      showToastSuccess,
      showToastError,
      refBetslipTable,
    };
  },
};
</script>

